import React, { useState } from 'react';
import {
  Plus, Search, Edit2, Trash2, Database, Activity, AlertTriangle,
  CheckCircle, Clock, HardDrive, Server, Zap, Settings, Eye,
  Building2, MapPin, User, Calendar, BarChart3, Shield, Cpu, Layers
} from 'lucide-react';
import { StorageUnit } from '../types';
import { apiService } from '../services/api';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const StorageUnitsPage: React.FC = () => {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('storage-units', setSearchTerm);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingStorage, setEditingStorage] = useState<StorageUnit | null>(null);
  const [selectedStorage, setSelectedStorage] = useState<StorageUnit | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [storageData, setStorageData] = useState<StorageUnit[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [usersData, setUsersData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [rawCapacityTb, setRawCapacityTb] = useState(10);

  // Pool management
  const [poolsData, setPoolsData] = useState<any[]>([]);
  const [showPoolModal, setShowPoolModal] = useState(false);
  const [editingPool, setEditingPool] = useState<any | null>(null);
  const [poolStorageId, setPoolStorageId] = useState<number>(0);
  const [poolForm, setPoolForm] = useState({ pool_name: '', disk_count: 0, raid_level: '', total_capacity_tb: 0, used_capacity_tb: 0, notes: '' });
  const [poolRawCapacityTb, setPoolRawCapacityTb] = useState(1);

  const [formData, setFormData] = useState({
    storage_name: '',
    storage_type: 'san' as StorageUnit['storage_type'],
    brand: '',
    model: '',
    serial_number: '',
    ip_address: '',
    management_ip: '',
    total_capacity_tb: 10,
    used_capacity_tb: 0,
    available_capacity_tb: 10,
    raid_level: '',
    disk_count: 0,
    disk_type: 'ssd' as StorageUnit['disk_type'],
    connection_type: 'iscsi' as StorageUnit['connection_type'],
    location_id: 0,
    company_id: 0,
    rack_position: '',
    power_consumption_watts: 0,
    warranty_end_date: '',
    purchase_date: '',
    purchase_price: 0,
    currency: 'TRY' as 'TRY' | 'USD' | 'EUR',
    status: 'active' as StorageUnit['status'],
    assigned_admin_id: 0,
    backup_schedule: '',
    next_maintenance: '',
    encryption_enabled: false,
    compression_enabled: false,
    deduplication_enabled: false,
    notes: ''
  });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  // Bildirimden belirli bir depolama birimi açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'storage-units' || !e.detail?.entityId) return;
      try {
        const storage = await apiService.getStorageUnitById(e.detail.entityId);
        if (storage) { setSelectedStorage(storage); setShowDetailModal(true); }
      } catch (err) { console.error('[openEntityById storageunits]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [storageResponse, companiesResponse, locationsResponse, usersResponse] = await Promise.all([
        apiService.getStorageUnits(),
        apiService.getCompanies(),
        apiService.getLocations(),
        apiService.getUsers()
      ]);
      // API response format kontrolü
      setStorageData(Array.isArray(storageResponse) ? storageResponse : (storageResponse?.data || []));
      setCompaniesData(Array.isArray(companiesResponse) ? companiesResponse : (companiesResponse?.data || []));
      setLocationsData(Array.isArray(locationsResponse) ? locationsResponse : (locationsResponse?.data || []));
      setUsersData(Array.isArray(usersResponse) ? usersResponse.filter(u => u.role === 'admin' || u.role === 'superadmin') : (usersResponse?.data?.filter(u => u.role === 'admin' || u.role === 'superadmin') || []));
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('storage.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'active', label: t('storage.statusActive'), color: 'bg-green-100 text-green-800' },
    { value: 'maintenance', label: t('storage.statusMaintenance'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'offline', label: t('storage.statusOffline'), color: 'bg-red-100 text-red-800' },
    { value: 'decommissioned', label: t('storage.statusDecommissioned'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' }
  ];

  const typeOptions = [
    { value: 'san', label: 'SAN' },
    { value: 'nas', label: 'NAS' },
    { value: 'backup', label: 'Backup' },
    { value: 'object', label: 'Object Storage' },
    { value: 'tape', label: 'Tape Library' }
  ];

  const diskTypeOptions = [
    { value: 'ssd', label: 'SSD' },
    { value: 'hdd', label: 'HDD' },
    { value: 'nvme', label: 'NVMe' },
    { value: 'hybrid', label: 'Hybrid' }
  ];

  const connectionTypeOptions = [
    { value: 'iscsi', label: 'iSCSI' },
    { value: 'fc', label: 'Fibre Channel' },
    { value: 'nfs', label: 'NFS' },
    { value: 'smb', label: 'SMB/CIFS' },
    { value: 'ftp', label: 'FTP' },
    { value: 'http', label: 'HTTP/REST' }
  ];

  const formatDateForInput = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try { return new Date(dateStr).toISOString().split('T')[0]; } catch { return ''; }
  };

  const loadPools = async (storageId: number) => {
    try {
      const data = await apiService.getStoragePools(storageId);
      setPoolsData(Array.isArray(data) ? data : []);
    } catch { setPoolsData([]); }
  };

  const handleOpenPoolModal = (storage: StorageUnit) => {
    setPoolStorageId(storage.id);
    loadPools(storage.id);
    setSelectedStorage(storage);
    setShowDetailModal(true);
  };

  const handleAddPool = () => {
    setEditingPool(null);
    setPoolRawCapacityTb(1);
    setPoolForm({ pool_name: '', disk_count: 0, raid_level: '', total_capacity_tb: 0, used_capacity_tb: 0, notes: '' });
    setShowPoolModal(true);
  };

  const handleEditPool = (pool: any) => {
    setEditingPool(pool);
    // Estimate raw per disk from existing capacity when editing
    const rawPerDisk = pool.disk_count > 0 ? pool.total_capacity_tb / pool.disk_count : 1;
    setPoolRawCapacityTb(Math.round(rawPerDisk * 100) / 100);
    setPoolForm({
      pool_name: pool.pool_name,
      disk_count: pool.disk_count,
      raid_level: pool.raid_level || '',
      total_capacity_tb: pool.total_capacity_tb,
      used_capacity_tb: pool.used_capacity_tb,
      notes: pool.notes || ''
    });
    setShowPoolModal(true);
  };

  const handleSavePool = async () => {
    if (!poolForm.pool_name || poolForm.disk_count <= 0) {
      showGlobalNotification.error(t('common.error'), 'Pool adı ve disk sayısı zorunludur');
      return;
    }
    try {
      if (editingPool) {
        await apiService.updateStoragePool(poolStorageId, editingPool.id, poolForm);
        showGlobalNotification.success(t('common.success'), 'Pool güncellendi');
      } else {
        await apiService.createStoragePool(poolStorageId, poolForm);
        showGlobalNotification.success(t('common.success'), 'Pool oluşturuldu');
      }
      setShowPoolModal(false);
      await loadPools(poolStorageId);
    } catch (error: any) {
      showGlobalNotification.error(t('common.error'), error.message || 'Pool kaydedilemedi');
    }
  };

  const handleDeletePool = async (poolId: number) => {
    if (!confirm('Bu pool\'u silmek istediğinizden emin misiniz?')) return;
    try {
      await apiService.deleteStoragePool(poolStorageId, poolId);
      showGlobalNotification.success(t('common.success'), 'Pool silindi');
      await loadPools(poolStorageId);
    } catch (error: any) {
      showGlobalNotification.error(t('common.error'), error.message || 'Pool silinemedi');
    }
  };

  const raidOptions = [
    { value: '', label: t('storage.raidNone') },
    { value: 'RAID 0',  label: 'RAID 0 — Striping (redundans yok)' },
    { value: 'RAID 1',  label: 'RAID 1 — Mirroring (%50 kullanılabilir)' },
    { value: 'RAID 5',  label: 'RAID 5 — Striping + Tek Parity (min. 3 disk)' },
    { value: 'RAID 6',  label: 'RAID 6 — Striping + Çift Parity (min. 4 disk)' },
    { value: 'RAID 10', label: 'RAID 10 — Mirror + Stripe (%50 kullanılabilir)' },
    { value: 'RAID 50', label: 'RAID 50 (min. 6 disk)' },
    { value: 'RAID 60', label: 'RAID 60 (min. 8 disk)' },
  ];

  const calculateUsableCapacity = (rawTb: number, raidLevel: string, diskCount: number): number => {
    const n = diskCount > 0 ? diskCount : 1;
    switch (raidLevel) {
      case 'RAID 0':  return rawTb;
      case 'RAID 1':  return rawTb * 0.5;
      case 'RAID 5':  return n >= 3 ? rawTb * (n - 1) / n : rawTb;
      case 'RAID 6':  return n >= 4 ? rawTb * (n - 2) / n : rawTb;
      case 'RAID 10': return rawTb * 0.5;
      case 'RAID 50': return n >= 6 ? rawTb * (n - 2) / n : rawTb;
      case 'RAID 60': return n >= 8 ? rawTb * (n - 4) / n : rawTb;
      default:        return rawTb;
    }
  };

  const isRaidDiskCountValid = (raidLevel: string, diskCount: number): boolean => {
    if (!raidLevel) return true;
    switch (raidLevel) {
      case 'RAID 5':  return diskCount >= 3;
      case 'RAID 6':  return diskCount >= 4;
      case 'RAID 50': return diskCount >= 6;
      case 'RAID 60': return diskCount >= 8;
      default:        return true;
    }
  };

  const filteredStorageUnits = storageData.filter(storage => {
    const matchesSearch = storage.storage_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         storage.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         storage.serial_number.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || storage.storage_type === filterType;
    const matchesStatus = filterStatus === 'all' || storage.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleEdit = (storage: StorageUnit) => {
    setEditingStorage(storage);
    setRawCapacityTb(storage.total_capacity_tb);
    setFormData({
      storage_name: storage.storage_name,
      storage_type: storage.storage_type,
      brand: storage.brand,
      model: storage.model,
      serial_number: storage.serial_number,
      ip_address: storage.ip_address || '',
      management_ip: storage.management_ip || '',
      total_capacity_tb: storage.total_capacity_tb,
      used_capacity_tb: storage.used_capacity_tb,
      available_capacity_tb: storage.available_capacity_tb,
      raid_level: storage.raid_level || '',
      disk_count: storage.disk_count || 0,
      disk_type: storage.disk_type,
      connection_type: storage.connection_type,
      location_id: storage.location_id,
      company_id: storage.company_id,
      rack_position: storage.rack_position || '',
      power_consumption_watts: storage.power_consumption_watts || 0,
      warranty_end_date: formatDateForInput(storage.warranty_end_date),
      purchase_date: formatDateForInput(storage.purchase_date),
      purchase_price: storage.purchase_price || 0,
      currency: storage.currency || 'TRY',
      status: storage.status,
      assigned_admin_id: storage.assigned_admin_id || 0,
      backup_schedule: storage.backup_schedule || '',
      next_maintenance: storage.next_maintenance || '',
      encryption_enabled: !!storage.encryption_enabled,
      compression_enabled: !!storage.compression_enabled,
      deduplication_enabled: !!storage.deduplication_enabled,
      notes: storage.notes || ''
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingStorage(null);
    setRawCapacityTb(10);
    setFormData({
      storage_name: '',
      storage_type: 'san',
      brand: '',
      model: '',
      serial_number: '',
      ip_address: '',
      management_ip: '',
      total_capacity_tb: 10,
      used_capacity_tb: 0,
      available_capacity_tb: 10,
      raid_level: '',
      disk_count: 0,
      disk_type: 'ssd',
      connection_type: 'iscsi',
      location_id: 0,
      company_id: 0,
      rack_position: '',
      power_consumption_watts: 0,
      warranty_end_date: '',
      purchase_date: '',
      purchase_price: 0,
      currency: 'TRY',
      status: 'active',
      assigned_admin_id: currentUser?.id || 0,
      backup_schedule: '',
      next_maintenance: '',
      encryption_enabled: false,
      compression_enabled: false,
      deduplication_enabled: false,
      notes: ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.storage_name || !formData.brand || !formData.model || !formData.serial_number) {
      showGlobalNotification.error(t('common.error'), t('storage.errorRequired'));
      return;
    }

    try {
      if (editingStorage) {
        // Update existing storage unit
        await apiService.updateStorageUnit(editingStorage.id, formData);
        showGlobalNotification.success(t('common.success'), t('storage.successUpdate'));
      } else {
        // Add new storage unit
        await apiService.createStorageUnit(formData);
        showGlobalNotification.success(t('common.success'), t('storage.successCreate'));
      }

      // Reload data
      await loadData();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving storage unit:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('storage.errorSave'));
    }
  };

  const handleDelete = async (id: number) => {
    const storageToDelete = storageData.find(s => s.id === id);
    if (!storageToDelete) return;
    
    if (confirm(t('storage.confirmDelete'))) {
      try {
        await apiService.deleteStorageUnit(id);
        await loadData();
        showGlobalNotification.success(t('common.success'), t('storage.successDelete'));
      } catch (error: any) {
        console.error('Error deleting storage unit:', error);
        showGlobalNotification.error(t('common.error'), error.message || t('storage.errorDelete'));
      }
    }
  };

  const showStorageDetail = (storage: StorageUnit) => {
    setSelectedStorage(storage);
    setPoolStorageId(storage.id);
    loadPools(storage.id);
    setShowDetailModal(true);
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getUsageColor = (percentage: number) => {
    if (percentage > 90) return 'text-red-600';
    if (percentage > 75) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getUsageBarColor = (percentage: number) => {
    if (percentage > 90) return 'bg-red-500';
    if (percentage > 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('storage.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('storage.subtitle')}</p>
        </div>
        <button
          onClick={handleAdd}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors mt-4 sm:mt-0"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('storage.addNew')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('storage.totalUnits')}</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{storageData.length}</p>
            </div>
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('storage.totalCapacity')}</p>
              <p className="text-xl sm:text-2xl font-bold text-primary">
                {storageData.reduce((sum, s) => sum + s.total_capacity_tb, 0)} TB
              </p>
            </div>
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-primary/20 rounded-lg flex items-center justify-center">
              <HardDrive className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('storage.usedCapacity')}</p>
              <p className="text-xl sm:text-2xl font-bold text-orange-600">
                {storageData.reduce((sum, s) => sum + s.used_capacity_tb, 0)} TB
              </p>
            </div>
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('storage.availableCapacity')}</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">
                {storageData.reduce((sum, s) => sum + s.available_capacity_tb, 0)} TB
              </p>
            </div>
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('storage.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="all">{t('storage.allTypes')}</option>
          {typeOptions.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="all">{t('storage.allStatuses')}</option>
          {statusOptions.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Storage Units Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredStorageUnits.map((storage) => {
          const usagePercentage = (storage.used_capacity_tb / storage.total_capacity_tb) * 100;
          return (
            <div
              key={storage.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center min-w-0 flex-1 mr-2">
                  <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center shrink-0">
                    <Database className="w-5 h-5 text-teal-600" />
                  </div>
                  <div className="ml-3 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {storage.storage_name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{storage.brand} {storage.model}</p>
                  </div>
                </div>
                <div className="flex shrink-0">
                  <button
                    onClick={() => showStorageDetail(storage)}
                    className="p-2 text-gray-400 hover:text-primary transition-colors"
                    title={t('storage.viewDetails')}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleEdit(storage)}
                    className="p-2 text-gray-400 hover:text-primary transition-colors"
                    title="Düzenle"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(storage.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                    title="Sil"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('storage.typeLabel')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 uppercase">{storage.storage_type}</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">{t('storage.totalCapacityLabel')}</span>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{storage.total_capacity_tb} TB</span>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('storage.usageRate')}</span>
                    <span className={`text-sm font-medium ${getUsageColor(usagePercentage)}`}>
                      {usagePercentage.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getUsageBarColor(usagePercentage)}`}
                      style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('storage.usedLabel')}</p>
                    <p className="text-sm font-semibold text-orange-600">
                      {storage.used_capacity_tb} TB
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('storage.availableLabel')}</p>
                    <p className="text-sm font-semibold text-green-600">
                      {storage.available_capacity_tb} TB
                    </p>
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                  {storage.encryption_enabled && (
                    <span className="inline-flex items-center px-2 py-1 text-xs bg-primary/20 text-primary-dark rounded-full">
                      <Shield className="w-3 h-3 mr-1" />
                      {t('storage.encrypted')}
                    </span>
                  )}
                  {storage.compression_enabled && (
                    <span className="inline-flex items-center px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                      {t('storage.compression')}
                    </span>
                  )}
                  {storage.deduplication_enabled && (
                    <span className="inline-flex items-center px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded-full">
                      Dedup
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(storage.status)}`}>
                    {statusOptions.find(s => s.value === storage.status)?.label}
                  </span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 dark:text-gray-400">
                    {new Date(storage.created_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Storage Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingStorage ? t('storage.modalEditTitle') : t('storage.modalAddTitle')}
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                ✕
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldName')}
                  </label>
                  <input
                    type="text"
                    value={formData.storage_name}
                    onChange={(e) => setFormData({...formData, storage_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="SAN-PRIMARY-01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldType')}
                  </label>
                  <select
                    value={formData.storage_type}
                    onChange={(e) => setFormData({...formData, storage_type: e.target.value as StorageUnit['storage_type']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {typeOptions.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldBrand')}
                  </label>
                  <input
                    type="text"
                    value={formData.brand}
                    onChange={(e) => setFormData({...formData, brand: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="NetApp"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldModel')}
                  </label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="FAS2750"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldSerial')}
                  </label>
                  <input
                    type="text"
                    value={formData.serial_number}
                    onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="NT2024ST001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldIP')}
                  </label>
                  <input
                    type="text"
                    value={formData.ip_address}
                    onChange={(e) => setFormData({...formData, ip_address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="192.168.1.100"
                  />
                </div>

                {/* Ham Kapasite */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldRawCapacity')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={rawCapacityTb}
                    onChange={(e) => {
                      const raw = parseFloat(e.target.value) || 1;
                      setRawCapacityTb(raw);
                      const usable = calculateUsableCapacity(raw, formData.raid_level, formData.disk_count);
                      setFormData({ ...formData, total_capacity_tb: Math.round(usable * 100) / 100 });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* RAID Seviyesi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldRaidLevel')}
                  </label>
                  <select
                    value={formData.raid_level}
                    onChange={(e) => {
                      const raid = e.target.value;
                      const usable = calculateUsableCapacity(rawCapacityTb, raid, formData.disk_count);
                      setFormData({ ...formData, raid_level: raid, total_capacity_tb: Math.round(usable * 100) / 100 });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {raidOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Disk Sayısı */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldDiskCount')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.disk_count}
                    onChange={(e) => {
                      const diskCount = parseInt(e.target.value) || 0;
                      const usable = calculateUsableCapacity(rawCapacityTb, formData.raid_level, diskCount);
                      setFormData({ ...formData, disk_count: diskCount, total_capacity_tb: Math.round(usable * 100) / 100 });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* Kullanılan Kapasite */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldUsedCapacity')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    max={formData.total_capacity_tb}
                    value={formData.used_capacity_tb}
                    onChange={(e) => {
                      const usedCapacity = parseFloat(e.target.value) || 0;
                      setFormData({ ...formData, used_capacity_tb: usedCapacity });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* RAID Kapasite Bilgi Kutusu — tam genişlik */}
                <div className="md:col-span-2">
                  {formData.raid_level ? (
                    isRaidDiskCountValid(formData.raid_level, formData.disk_count) ? (
                      <div className="flex items-center gap-3 p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-lg">
                        <HardDrive className="w-5 h-5 text-teal-600 dark:text-teal-400 shrink-0" />
                        <div className="text-sm text-teal-800 dark:text-teal-200">
                          <span className="font-medium">{formData.raid_level}</span>
                          {' — '}
                          <span>{t('storage.raidUsableLabel')}:</span>
                          {' '}
                          <span className="font-bold text-teal-700 dark:text-teal-300">{formData.total_capacity_tb} TB</span>
                          {formData.disk_count > 0 && (
                            <span className="ml-2 text-teal-600 dark:text-teal-400">
                              ({formData.disk_count} × {(rawCapacityTb / formData.disk_count).toFixed(1)} TB/disk)
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0" />
                        <span className="text-sm text-amber-800 dark:text-amber-200">{t('storage.raidUsableNotice')}</span>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
                      <HardDrive className="w-5 h-5 text-gray-400 shrink-0" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {t('storage.raidUsableLabel')}: <span className="font-bold text-gray-700 dark:text-gray-300">{formData.total_capacity_tb} TB</span>
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldDiskType')}
                  </label>
                  <select
                    value={formData.disk_type}
                    onChange={(e) => setFormData({...formData, disk_type: e.target.value as StorageUnit['disk_type']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {diskTypeOptions.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldConnectionType')}
                  </label>
                  <select
                    value={formData.connection_type}
                    onChange={(e) => setFormData({...formData, connection_type: e.target.value as StorageUnit['connection_type']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {connectionTypeOptions.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldCompany')}
                  </label>
                  <select
                    value={formData.company_id}
                    onChange={(e) => setFormData({...formData, company_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('storage.selectCompany')}</option>
                    {companiesData.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldLocation')}
                  </label>
                  <select
                    value={formData.location_id}
                    onChange={(e) => setFormData({...formData, location_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('storage.selectLocation')}</option>
                    {locationsData.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldStatus')}
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value as StorageUnit['status']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {statusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldPurchaseDate')}
                  </label>
                  <input
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('storage.fieldWarrantyEnd')}
                  </label>
                  <input
                    type="date"
                    value={formData.warranty_end_date}
                    onChange={(e) => setFormData({...formData, warranty_end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              {/* Features */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('storage.featuresTitle')}</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.encryption_enabled}
                      onChange={(e) => setFormData({...formData, encryption_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-teal-600 shadow-sm focus:border-teal-300 focus:ring focus:ring-teal-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('storage.featureEncryption')}</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.compression_enabled}
                      onChange={(e) => setFormData({...formData, compression_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-teal-600 shadow-sm focus:border-teal-300 focus:ring focus:ring-teal-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('storage.featureCompression')}</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.deduplication_enabled}
                      onChange={(e) => setFormData({...formData, deduplication_enabled: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-teal-600 shadow-sm focus:border-teal-300 focus:ring focus:ring-teal-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('storage.featureDeduplication')}</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('storage.fieldNotes')}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder={t('storage.notesPlaceholder')}
                />
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
              >
                {t('storage.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                {editingStorage ? t('storage.update') : t('storage.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Storage Detail Modal */}
      {showDetailModal && selectedStorage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[95vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('storage.detailTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate max-w-[220px] sm:max-w-none">
                    {selectedStorage.storage_name}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors ml-2"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('storage.detailInfoSection')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('storage.detailNameLabel')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedStorage.storage_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('storage.detailTypeLabel')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 uppercase">{selectedStorage.storage_type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('storage.detailBrandModelLabel')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedStorage.brand} {selectedStorage.model}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('storage.detailTotalCapacityLabel')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedStorage.total_capacity_tb} TB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('storage.detailDiskTypeLabel')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100 uppercase">{selectedStorage.disk_type}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('storage.detailCapacitySection')}</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Toplam Disk</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedStorage.disk_count || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">RAID</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedStorage.raid_level || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Kullanılabilir</span>
                      <span className="font-medium text-green-600">{selectedStorage.available_capacity_tb} TB</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Kullanılan</span>
                      <span className="font-medium text-orange-600">{selectedStorage.used_capacity_tb} TB</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pool Management */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-start justify-between mb-3 gap-2">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                      <Layers className="w-4 h-4 text-teal-600 shrink-0" />
                      Disk Havuzları (Pools)
                    </h3>
                    {selectedStorage.disk_count > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 ml-6">
                        Toplam {selectedStorage.disk_count} disk — {poolsData.reduce((s, p) => s + p.disk_count, 0)} kullanımda, {selectedStorage.disk_count - poolsData.reduce((s, p) => s + p.disk_count, 0)} boşta
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleAddPool}
                    className="flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors shrink-0"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Havuz Ekle
                  </button>
                </div>

                {poolsData.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-4">Henüz havuz oluşturulmadı</p>
                ) : (
                  <div className="space-y-2">
                    {poolsData.map(pool => {
                      const usedPct = pool.total_capacity_tb > 0 ? (pool.used_capacity_tb / pool.total_capacity_tb) * 100 : 0;
                      return (
                        <div key={pool.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                          <Layers className="w-4 h-4 text-teal-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{pool.pool_name}</span>
                              {pool.raid_level && <span className="text-xs bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 px-1.5 py-0.5 rounded">{pool.raid_level}</span>}
                              <span className="text-xs text-gray-500 dark:text-gray-400">{pool.disk_count} disk</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                                <div className={`h-1.5 rounded-full ${usedPct > 90 ? 'bg-red-500' : usedPct > 75 ? 'bg-yellow-500' : 'bg-teal-500'}`} style={{ width: `${Math.min(usedPct, 100)}%` }} />
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">{pool.used_capacity_tb}/{pool.total_capacity_tb} TB</span>
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button onClick={() => handleEditPool(pool)} className="p-1 text-gray-400 hover:text-primary transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleDeletePool(pool.id)} className="p-1 text-gray-400 hover:text-red-600 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pool Add/Edit Modal */}
      {showPoolModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[95vh] flex flex-col">
            <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {editingPool ? 'Havuz Düzenle' : 'Yeni Havuz Ekle'}
              </h3>
              <button onClick={() => setShowPoolModal(false)} className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                ✕
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Havuz Adı *</label>
                <input type="text" value={poolForm.pool_name}
                  onChange={e => setPoolForm({...poolForm, pool_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Pool-A" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Disk Sayısı *</label>
                  <input type="number" min="1" value={poolForm.disk_count}
                    onChange={e => {
                      const diskCount = parseInt(e.target.value) || 0;
                      const usable = calculateUsableCapacity(poolRawCapacityTb * diskCount, poolForm.raid_level, diskCount);
                      setPoolForm({...poolForm, disk_count: diskCount, total_capacity_tb: Math.round(usable * 100) / 100});
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Disk Başına Kapasite (TB)</label>
                  <input type="number" min="0.1" step="0.1" value={poolRawCapacityTb}
                    onChange={e => {
                      const raw = parseFloat(e.target.value) || 0;
                      setPoolRawCapacityTb(raw);
                      const usable = calculateUsableCapacity(raw * poolForm.disk_count, poolForm.raid_level, poolForm.disk_count);
                      setPoolForm({...poolForm, total_capacity_tb: Math.round(usable * 100) / 100});
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">RAID Seviyesi</label>
                <select value={poolForm.raid_level}
                  onChange={e => {
                    const raid = e.target.value;
                    const usable = calculateUsableCapacity(poolRawCapacityTb * poolForm.disk_count, raid, poolForm.disk_count);
                    setPoolForm({...poolForm, raid_level: raid, total_capacity_tb: Math.round(usable * 100) / 100});
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                  {raidOptions.map(o => <option key={o.value} value={o.value}>{o.value || 'RAID yok'}</option>)}
                </select>
              </div>
              {poolForm.disk_count > 0 && poolRawCapacityTb > 0 && (
                <div className="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-md text-sm space-y-1">
                  <div className="flex justify-between flex-wrap gap-1">
                    <span className="text-teal-700 dark:text-teal-300">Ham Kapasite:</span>
                    <span className="font-medium text-teal-800 dark:text-teal-200">{(poolRawCapacityTb * poolForm.disk_count).toFixed(1)} TB ({poolForm.disk_count} × {poolRawCapacityTb} TB)</span>
                  </div>
                  <div className="flex justify-between flex-wrap gap-1">
                    <span className="text-teal-700 dark:text-teal-300">Kullanılabilir Kapasite:</span>
                    <span className="font-bold text-teal-800 dark:text-teal-200">{poolForm.total_capacity_tb} TB</span>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Toplam Kapasite (TB) *</label>
                  <input type="number" min="0" step="0.1" value={poolForm.total_capacity_tb}
                    onChange={e => setPoolForm({...poolForm, total_capacity_tb: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kullanılan (TB)</label>
                  <input type="number" min="0" step="0.1" value={poolForm.used_capacity_tb}
                    onChange={e => setPoolForm({...poolForm, used_capacity_tb: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notlar</label>
                <textarea rows={2} value={poolForm.notes}
                  onChange={e => setPoolForm({...poolForm, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-teal-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
              </div>
            </div>
            <div className="p-4 sm:p-5 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3 shrink-0">
              <button onClick={() => setShowPoolModal(false)} className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 transition-colors">İptal</button>
              <button onClick={handleSavePool} className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors">{editingPool ? 'Güncelle' : 'Kaydet'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StorageUnitsPage;